import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Wifi, WifiOff, Receipt, CalendarClock, ArrowRight, CheckCircle2, AlertTriangle, ArrowDown, ArrowUp, Activity, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { usePortalAuth } from "./PortalAuthContext";
import { rupiah, portalApi, fmtBytes } from "./portalApi";
import { PortalPayModal } from "./PortalPayModal";

export const PortalHomePage = () => {
  const { customer, refresh } = usePortalAuth();
  const [payInv, setPayInv] = useState(null);
  const [usage, setUsage] = useState(null);
  const [harian, setHarian] = useState([]);
  const c = customer || {};
  const paket = c.paket;
  const inv = c.tagihan_berjalan;
  const r = c.ringkasan || {};
  const aktif = c.status_layanan === "aktif";
  const bulanIni = new Date().toLocaleDateString("id-ID", { month: "long", year: "numeric" });

  useEffect(() => {
    portalApi.get("/usage").then((res) => setUsage(res.data)).catch(() => {});
    portalApi.get("/usage/harian").then((res) => setHarian(res.data?.data || [])).catch(() => {});
  }, []);

  const chartData = harian.map((d) => ({
    hari: d.hari,
    Download: +(d.download_bytes / 1e9).toFixed(2),
    Upload: +(d.upload_bytes / 1e9).toFixed(2),
  }));
  const adaData = chartData.some((d) => d.Download > 0 || d.Upload > 0);

  return (
    <div className="space-y-4" data-testid="portal-home">
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#0d1527] to-[#0a1222] border border-slate-800">
        <p className="text-xs text-slate-400">Selamat datang,</p>
        <h1 className="text-xl font-bold text-white" data-testid="portal-home-name">{c.nama}</h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">{c.no_layanan}</span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded border flex items-center gap-1 ${aktif ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`} data-testid="portal-status-layanan">
            {aktif ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {String(c.status_layanan || "").toUpperCase()}
          </span>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-[#0d1527] border border-slate-800" data-testid="portal-paket-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2"><Package className="w-4 h-4 text-cyan-400" /> Paket Layanan</h2>
          <Link to="/portal/upgrade" className="text-xs text-cyan-400 flex items-center gap-1 hover:underline">Upgrade <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <p className="text-lg font-bold text-white">{paket?.nama || c.paket_nama || "-"}</p>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <div className="p-2 rounded-xl bg-slate-900"><p className="text-[10px] text-slate-500">Kecepatan</p><p className="text-sm font-bold text-cyan-300">{paket?.kecepatan || "-"}</p></div>
          <div className="p-2 rounded-xl bg-slate-900"><p className="text-[10px] text-slate-500">Download / Upload</p><p className="text-sm font-bold text-white">{paket ? `${paket.download_speed}/${paket.upload_speed} Mbps` : "-"}</p></div>
          <div className="p-2 rounded-xl bg-slate-900"><p className="text-[10px] text-slate-500">Biaya / bulan</p><p className="text-sm font-bold text-amber-300">{rupiah(paket?.harga ?? c.paket_harga)}</p></div>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-[#0d1527] border border-slate-800" data-testid="portal-usage-card">
        <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-indigo-400" /> Pemakaian Internet — {bulanIni}</h2>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="p-3 rounded-xl bg-slate-900">
            <p className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><ArrowDown className="w-3 h-3 text-emerald-400" /> Total Download</p>
            <p className="text-lg font-bold text-emerald-300 font-mono" data-testid="portal-usage-download">{usage ? fmtBytes(usage.download_bytes) : "-"}</p>
          </div>
          <div className="p-3 rounded-xl bg-slate-900">
            <p className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><ArrowUp className="w-3 h-3 text-sky-400" /> Total Upload</p>
            <p className="text-lg font-bold text-sky-300 font-mono" data-testid="portal-usage-upload">{usage ? fmtBytes(usage.upload_bytes) : "-"}</p>
          </div>
        </div>
        {usage?.source === "live" && (
          <div className="mt-2 flex items-center justify-center">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${usage.online ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-slate-700/50 text-slate-400 border border-slate-600"}`} data-testid="portal-usage-online">
              <span className={`w-1.5 h-1.5 rounded-full ${usage.online ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} /> {usage.online ? "Sesi PPPoE Aktif" : "PPPoE Offline"}
            </span>
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2 text-center">
          {usage?.source === "live"
            ? (usage.online
                ? "Data langsung (live) dari router MikroTik — sesi PPPoE Anda saat ini. Pakai tombol refresh halaman untuk memperbarui."
                : "Router terhubung namun sesi PPPoE Anda sedang offline. Hubungkan perangkat untuk melihat pemakaian.")
            : usage?.tracked
            ? `Akumulasi bulanan dari router — tidak tereset saat router restart, hanya berganti saat ganti bulan. Diperbarui ${usage.updated_at ? new Date(usage.updated_at).toLocaleString("id-ID") : ""}.`
            : "Data pemakaian akan tampil otomatis saat perangkat Anda terhubung ke PPPoE router."}
        </p>
      </div>

      <div className="p-4 rounded-2xl bg-[#0d1527] border border-slate-800" data-testid="portal-usage-chart-card">
        <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-cyan-400" /> Pola Pemakaian Harian — {bulanIni}</h2>
        {adaData ? (
          <div style={{ width: "100%", height: 220 }} data-testid="portal-usage-chart">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 4, right: 6, left: -18, bottom: 0 }}>
                <XAxis dataKey="hari" tick={{ fontSize: 9, fill: "#64748b" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} width={34} unit="G" />
                <Tooltip
                  contentStyle={{ background: "#0b1222", border: "1px solid #1e293b", borderRadius: 12, fontSize: 11 }}
                  labelStyle={{ color: "#e2e8f0" }}
                  labelFormatter={(v) => `Tanggal ${v}`}
                  formatter={(val, name) => [`${val} GB`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" />
                <Bar dataKey="Download" fill="#34d399" radius={[3, 3, 0, 0]} maxBarSize={18} />
                <Bar dataKey="Upload" fill="#38bdf8" radius={[3, 3, 0, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-[11px] text-slate-500 text-center py-8">Belum ada data pemakaian harian bulan ini. Grafik akan terisi otomatis seiring pemakaian internet Anda (data direkam berkala dari router).</p>
        )}
      </div>

      <div className={`p-4 rounded-2xl border ${inv ? "bg-rose-500/5 border-rose-500/30" : "bg-emerald-500/5 border-emerald-500/30"}`} data-testid="portal-tagihan-berjalan">
        <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-2"><Receipt className="w-4 h-4 text-amber-400" /> Tagihan Berjalan</h2>
        {inv ? (
          <>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs text-slate-400">{inv.periode_bulan} • <span className="font-mono">{inv.no_invoice}</span></p>
                <p className="text-2xl font-bold text-white font-mono mt-1">{rupiah(inv.sisa_tagihan)}</p>
                <p className="text-[11px] text-rose-300 flex items-center gap-1 mt-1"><CalendarClock className="w-3 h-3" /> Jatuh tempo {inv.jatuh_tempo}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-rose-400 shrink-0" />
            </div>
            <button onClick={() => setPayInv(inv)} className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold text-sm" data-testid="portal-bayar-btn">Bayar Sekarang</button>
          </>
        ) : (
          <div className="flex items-center gap-2 text-emerald-300 text-sm"><CheckCircle2 className="w-5 h-5" /> Tidak ada tagihan tertunggak. Terima kasih!</div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="portal-ringkasan">
        <div className="p-3 rounded-xl bg-[#0d1527] border border-slate-800 text-center"><p className="text-lg font-bold text-white">{r.total_invoice ?? 0}</p><p className="text-[10px] text-slate-500">Total Invoice</p></div>
        <div className="p-3 rounded-xl bg-[#0d1527] border border-slate-800 text-center"><p className="text-lg font-bold text-emerald-400">{r.lunas ?? 0}</p><p className="text-[10px] text-slate-500">Lunas</p></div>
        <div className="p-3 rounded-xl bg-[#0d1527] border border-slate-800 text-center"><p className="text-lg font-bold text-rose-400">{r.belum_bayar ?? 0}</p><p className="text-[10px] text-slate-500">Belum Bayar</p></div>
      </div>

      {payInv && <PortalPayModal invoice={payInv} onClose={() => setPayInv(null)} onPaid={refresh} />}
    </div>
  );
};
